import RichText from "@/components/common/richText/richText.component";
import { cn } from "@/lib/utils";
import Image, { type StaticImageData } from "next/image";
import type { ReactNode } from "react";
import { accentClasses, type IndustryAccent } from "../industryTheme/industryTheme.util";

// One floating note over the hero picture (the preview's `.hchip`).
export interface HeroChip {
  // `**bold**` is drawn in the page accent.
  text: string;
  // "plain" = white card, "whatsapp" = WhatsApp-green bubble, "ink" = dark.
  variant: "plain" | "whatsapp" | "ink";
  // Position over the picture, in % of its width / height (may be negative
  // so the chip hangs over the edge, as in the preview).
  x: number;
  y: number;
}

interface IndustryHeroVisualProps {
  // Background of the rounded picture card.
  tone: "blush" | "lilac" | "mint" | "sun" | "sky";
  chips: HeroChip[];
  accent?: IndustryAccent;
  // Either an illustration file (+ alt)...
  image?: StaticImageData;
  alt?: string;
  // ...or a drawn picture (e.g. an <IndustryScene>).
  children?: ReactNode;
}

const toneClass = {
  blush: "bg-tone-blush",
  lilac: "bg-tone-lilac",
  mint: "bg-tone-mint",
  sun: "bg-tone-sun",
  sky: "bg-tone-sky",
};

const chipClass = {
  plain: "bg-white text-ink",
  whatsapp: "bg-tone-mint text-ink",
  ink: "bg-ink text-white",
};

// Industry hero picture shared by every 2.0 industry page: the page's
// illustration in a soft rounded card, with small notes floating over its
// edges. The notes repeat what the picture shows, so they're hidden from
// assistive tech, and on phones, where they'd cover the picture. (Chip
// positions are data, so they're the one inline style here.)
const IndustryHeroVisual = ({
  tone,
  chips,
  accent = "brand",
  image,
  alt = "",
  children,
}: IndustryHeroVisualProps) => (
  <div className="relative">
    <figure
      className={cn("m-0 overflow-hidden rounded-[28px] shadow-lift", toneClass[tone])}
    >
      {image ? (
        <Image
          src={image}
          alt={alt}
          priority
          sizes="(min-width: 1024px) 560px, 100vw"
          className="block h-auto w-full"
        />
      ) : (
        children
      )}
    </figure>
    {chips.map((chip) => (
      <span
        key={chip.text}
        aria-hidden="true"
        style={{ left: `${chip.x}%`, top: `${chip.y}%` }}
        className={cn(
          "absolute z-[3] hidden max-w-[210px] rounded-2xl px-3.5 py-2.5 text-[14px] leading-tight font-bold shadow-card sm:inline-flex",
          chipClass[chip.variant],
        )}
      >
        <span>
          <RichText
            text={chip.text}
            strongClassName={cn(
              "font-bold",
              chip.variant === "ink" ? "text-tone-peach" : accentClasses[accent].text,
            )}
          />
        </span>
      </span>
    ))}
  </div>
);

export default IndustryHeroVisual;
